import React from 'react';

/**
 * @typedef {Object} FieldUpdate
 * @property {string} name
 * The name of the field that was updated. The following formats are supported:
 * - `<string>` -> single field update
 * - `[sub:<part1>:<part2>...]<field-name>` -> sub field update
 * @property {string} previousValue The previous value of the field as a JSON string.
 * @property {string} nextValue The next value of the field as a JSON string
 *
 * @typedef {Object} SingleFieldUpdateUI
 * @property {'single'} type
 * @property {string} name The name of the field that was updated.
 * @property {string} previousValue The previous value of the field as a JSON string.
 * @property {string} nextValue The next value of the field as a JSON string
 *
 * @typedef {Object} SubFieldUpdateUI
 * @property {'sub'} type
 * @property {string} name The name of the sub list that was updated.
 * @property {Array<SingleFieldUpdateUI>} updates The updates that were made to the sub list.
 */

/**
 * Goes over the given list of field updates and processes them into UI-friendly data.
 * The following transformations are applied:
 * - Single field updates get their names translated
 * - Sub field updates are grouped by path, and get their names translated
 *
 * @param {Array<FieldUpdate>} updates
 * A list of updates to process.
 *
 * @param {(key: string) => string} t
 * A translation function that takes a field name and returns a translated version of it.
 * For example, `t('ArticleGroup') -> 'Article group'`.
 *
 * @returns {Array<SingleFieldUpdateUI | SubFieldUpdateUI>} The processed updates.
 */
export const processFieldUpdates = (updates, t) => {
    const processedUpdates = {single: [], sublists: {}};

    for (const item of updates) {
        if (item.name.startsWith('[sub:')) {
            // This is a sub field update
            // In this case the name is in the format: [sub:<part1>:<part2>...]<field-name>
            // For example, [sub:Article group:@Cans 2023]PricePerPalletFixed
            //                                            ^^^^^^^^^^^^^^^^^^^ field name
            //                   ^^^^^^^^^^^^^^^^^^^^^^^^ list name

            // Extract the (raw) list name and field names
            const [, rawListName, rawFieldName] = item.name.match(/\[sub:(.*)\](.*)/);

            const listName = rawListName
                .split(':')
                .map((part) => {
                    if (part.startsWith('@')) {
                        // Parts that start with @ are considered literal and shuold be used as-is (without the @ symbol)
                        return part.slice(1);
                    }

                    // All other parts are considered to be translation keys and should be translated
                    return t(part);
                })
                .join(' ');

            const fieldName = t(rawFieldName);

            processedUpdates.sublists[listName] ||= {type: 'sub', name: listName, updates: []};
            processedUpdates.sublists[listName].updates.push({
                name: fieldName,
                previousValue: item.previousValue,
                nextValue: item.nextValue,
            });

            continue;
        }

        // Otherwise, this is a single field update
        processedUpdates.single.push({
            ...item,
            name: t(item.name),
            type: 'single',
        });
    }

    return [...processedUpdates.single, ...Object.values(processedUpdates.sublists)];
};

/**
 * Renders a timeline entry's field update.
 * @param {object} param0 Props.
 * @param {SingleFieldUpdateUI | SubFieldUpdateUI} param0.update The update to render.
 * @param {number} param0.maxSubfields The max number of subfields to render when the update is a 'sub' update.
 */
export const TimelineEntryFieldUpdate = ({update, maxSubfields, t}) => {
    const formatValueUpdate = ({previousValue, nextValue}) => {
        // If there is no previous value, we don't show the "<from> to" part, only the next value
        if (_.isNil(previousValue)) return <strong>{nextValue}</strong>;

        return (
            <>
                <strong>{previousValue}</strong> {t('warehousing.timeline.to')} <strong>{nextValue}</strong>
            </>
        );
    };

    if (update.type === 'single') {
        return (
            <li key={update.name}>
                <strong>{update.name}</strong>: {formatValueUpdate(update)}
            </li>
        );
    }

    if (update.type === 'sub') {
        return (
            <li key={update.name}>
                <strong>{update.name}</strong>
                <ul>
                    {_.slice(update.updates, 0, maxSubfields).map((subUpdate) => (
                        <li key={subUpdate.name}>
                            <strong>{subUpdate.name}</strong>: {formatValueUpdate(subUpdate)}
                        </li>
                    ))}
                </ul>
            </li>
        );
    }

    return null;
};
